<template>
  <div class="manage-tags-modal">
    <base-modal-ex :active.sync="isActive"
      :animation-in="animationIn"
      :animation-out="animationOut"
      :animation-duration="animationDuration"
      :can-cancel="false"
      :min-width="'10vw'"
      :max-width="'10vw'"
      has-modal-card>
      <header class="modal-card-head">
        <p class="modal-card-title">Manage Tags</p>
      </header>

      <section class="modal-card-body">
        <div class="columns">
          <div class="column is-full">
            <div class="field">
              <label class="label is-required-label">Tag Name</label>
              <div class="control">
                <input class="input"
                  :class="{'is-danger': !isTagNameValid}"
                  type="text"
                  v-model="newTag.name"
                  placeholder="Add Name"
                  maxlength="30">
                <div class="help is-secondary">
                  {{ newTag.name.length }}/30
                </div>
                <div class="tags"
                  v-if="!isTagNameValid">
                  <span class="tag is-danger">
                    Name already in use, choose a different name
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns is-vcentered is-multiline">

          <div class="column is-3">
            <div class="field">
              <label class="label is-required-label">Add Short Tag</label>
              <div class="control">
                <input class="input"
                  :class="{'is-danger': !isShortTagValid}"
                  type="text"
                  v-model="newTag.shortTag"
                  placeholder="ST"
                  maxlength="5"
                  @input="newTag.shortTag = $event.target.value.toUpperCase()">
                <div class="help is-secondary">
                  {{ newTag.shortTag.length }}/5
                </div>
                <div class="tags"
                  v-if="!isShortTagValid">
                  <span class="tag is-danger">
                    Short Tag already in use, choose a different short tag
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="column is-7 has-text-centered">
            <div class="field">
              <label class="label">Select Colour</label>
              <div class="control">
                <input type="color"
                  v-model="newTag.color"
                  class="color-circle">
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="field">
              <div class="control pt-5">
                <button class="button is-fullwidth tooltip bordered has-text-weight-bold"
                  :class="{'is-static': !canAddTag}"
                  :data-tooltip="editingTagIndex === null ? 'Add Tag' : 'Update Tag'"
                  @click="addTag"
                  :disabled="!canAddTag">
                  <span class="icon has-text-primary">
                    <i v-if="editingTagIndex === null"
                      class="mdi mdi-plus mdi-28px" />
                    <i v-else
                      class="mdi mdi-content-save mdi-28px" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="content mt-5">
          <div class="box tag-item"
            v-for="(tag, index) in localTags.filter(tag => tag.isDeleted !== true)"
            :key="index">
            <span class="tag-short"
              :style="{ backgroundColor: tag.colorHex, color: getTextColor(tag.colorHex) }">{{ tag.shortTag }}</span>
            <span class="tag-name">{{ tag.tagName }}</span>
            <div class="buttons is-right"
              style="flex-wrap: nowrap;">
              <button v-if="editingTagIndex === index"
                class="button is-ghost is-small tooltip p-0 has-text-danger is-clickable mr-2"
                data-tooltip="Cancel Edit"
                @click="cancelEditTag">
                <span class="material-symbols-outlined">close</span>
              </button>
              <button v-else
                class="button is-ghost is-small tooltip p-0 has-text-primary is-clickable mr-2"
                data-tooltip="Edit"
                @click="editTag(tag)">
                <span class="material-symbols-outlined">edit</span>
              </button>

              <button class="button is-ghost is-small tooltip px-0 mr-1 has-text-grey-lighter"
                @click="deleteTag(tag.tagID)"
                data-tooltip="Delete"
                style="border: none; height: auto; background: transparent">
                <span class="material-symbols-rounded"
                  @mouseenter="$event.target.classList.add('has-text-danger')"
                  @mouseleave="$event.target.classList.remove('has-text-danger')">
                  delete
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot is-flex is-justify-content-flex-end">
        <div class="buttons">
          <!-- <button class="button tooltip"
            @click="handleModalClose"
            data-tooltip="Cancel">
            <span class="icon">
              <i class="mdi mdi-close mdi-20px" />
            </span>
            <span>Cancel</span>
          </button> -->
          <button class="button tooltip"
            data-tooltip="Close"
            @click="saveTags">
            <span class="icon">
              <!-- <i class="mdi mdi-content-save mdi-20px" /> -->
              <i class="mdi mdi-close mdi-20px" />
            </span>
            <span>Close</span>
          </button>
        </div>
      </footer>
    </base-modal-ex>
  </div>
</template>

<script>
import { BaseModalEx } from '@/components/BulmaModal'
import TagsService from './TagsService'
import EventBus from '../../components/EventBus'
import { getTextColor } from '@/utils/ColorFunctions'

export default {
  name: 'ManageTagsModal',
  components: {
    BaseModalEx
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    animationIn: {
      type: String,
      default: 'zoomIn'
    },
    animationOut: {
      type: String,
      default: 'zoomOut'
    },
    animationDuration: {
      type: [String, Number],
      default: 0.3
    },
    buttonLocation: {
      type: String,
      default: 'end' // valid values 'start' & 'end'
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    iconType: {
      type: String,
      default: 'warning'
    },
    iconName: {
      type: String,
      default: 'mdi-alert-decagram'
    },
    tags: {
      type: Array,
      default: () => []
    },
    tagCategoryId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isActive: this.active || false,
      localTags: JSON.parse(JSON.stringify(this.tags)),
      newTag: {
        name: '',
        shortTag: '',
        color: '#3498db'
      },
      editingTagIndex: null,
      isTagNameValid: true,
      isShortTagValid: true
    }
  },
  computed: {
    canAddTag() {
      const trimmedName = this.newTag.name.trim()
      const trimmedShortTag = this.newTag.shortTag.trim()

      return trimmedName.length > 0 && trimmedShortTag.length > 0 && this.isTagNameValid && this.isShortTagValid
    }
  },
  watch: {
    active(value) {
      this.isActive = value
      if (value) {
        this.localTags = JSON.parse(JSON.stringify(this.tags))
      }
    },
    tags(newTags) {
      this.localTags = JSON.parse(JSON.stringify(newTags))
    },
    'newTag.name': {
      handler: function (val) {
        if (val !== '' && val !== null) {
          const trimmedUpper = val.trim().toUpperCase()
          const tagsToCheck = this.localTags.filter((t) => t.isDeleted !== true)
          if (this.editingTagIndex !== null) {
            this.isTagNameValid = !tagsToCheck.some((t, idx) => idx !== this.editingTagIndex && (t.tagName || '').toUpperCase() === trimmedUpper)
          } else {
            this.isTagNameValid = !tagsToCheck.some((t) => (t.tagName || '').toUpperCase() === trimmedUpper)
          }
        }
      },
      immediate: false
    },
    'newTag.shortTag': {
      handler: function (val) {
        if (val !== '' && val !== null) {
          const trimmedUpper = val.trim().toUpperCase()
          const tagsToCheck = this.localTags.filter((t) => t.isDeleted !== true)
          if (this.editingTagIndex !== null) {
            this.isShortTagValid = !tagsToCheck.some((t, idx) => idx !== this.editingTagIndex && (t.shortTag || '').toUpperCase() === trimmedUpper)
          } else {
            this.isShortTagValid = !tagsToCheck.some((t) => (t.shortTag || '').toUpperCase() === trimmedUpper)
          }
        }
      },
      immediate: false
    }
  },
  methods: {
    handleModalClose() {
      this.localTags = this.localTags.filter((tag) => !tag.isNew)
      this.editingTagIndex = null
      this.newTag = {
        name: '',
        shortTag: '',
        color: '#3498db'
      }
      this.$emit('close')
    },
    addTag() {
      const trimmedName = this.newTag.name.trim()
      const trimmedShortTag = this.newTag.shortTag.trim()
      if (!trimmedName || !trimmedShortTag) {
        this.$toast.open({
          message: 'Please fill in both Tag Name and Short Tag.',
          type: 'is-danger',
          position: 'is-top',
          queue: false
        })
        return
      }
      if (this.editingTagIndex !== null) {
        this.localTags.splice(this.editingTagIndex, 1, {
          ...this.localTags[this.editingTagIndex],
          tagName: trimmedName,
          shortTag: trimmedShortTag,
          colorHex: this.newTag.color,
          tagCategoryID: this.tagCategoryId
        })
        this.editingTagIndex = null
        this.$toast.open({
          message: 'Tag is updated',
          type: 'is-success',
          position: 'is-top',
          queue: false
        })
      } else {
        this.localTags.push({
          tagID: '00000000-0000-0000-0000-000000000000',
          tagName: trimmedName,
          shortTag: trimmedShortTag,
          colorHex: this.newTag.color,
          tagCategoryID: this.tagCategoryId,
          isNew: true
        })
        this.$toast.open({
          message: 'Tag is created',
          type: 'is-success',
          position: 'is-top',
          queue: false
        })
      }
      // Reset input fields
      this.newTag.name = ''
      this.newTag.shortTag = ''
      this.newTag.color = '#3498db'
    },
    editTag(tag) {
      const idx = this.localTags.indexOf(tag)
      if (idx !== -1) {
        this.editingTagIndex = idx
        this.newTag = {
          name: tag.tagName,
          shortTag: tag.shortTag,
          color: tag.colorHex || '#3498db'
        }
      }
    },
    cancelEditTag() {
      this.editingTagIndex = null
      this.newTag = {
        name: '',
        shortTag: '',
        color: '#3498db'
      }
    },
    deleteTag(tagId) {
      this.localTags = this.localTags.map((tag) =>
        tag.tagID === tagId
          ? { ...tag, isDeleted: true } // Use consistent property name
          : tag
      )
      this.$toast.open({
        message: 'Tag is deleted',
        type: 'is-danger',
        position: 'is-top',
        queue: false
      })
    },
    async saveTags() {
      // Compare localTags and original tags (ignoring object reference)
      const original = JSON.stringify(this.tags)
      const current = JSON.stringify(this.localTags)
      if (original === current) {
        // No changes, just close and emit
        // this.$emit('save', this.localTags)
        this.$emit('close')
        return true
      }
      try {
        this.$showSpinner('Saving...')
        var result = await TagsService.postTags(this.localTags)
        if (result) {
          this.$toast.open({
            message: 'Save successful',
            type: 'is-success',
            position: 'is-top',
            queue: false
          })
        } else {
          this.$toast.open({
            message: 'Save failed',
            type: 'is-danger',
            position: 'is-top',
            queue: false
          })
        }
        return true
      } catch (e) {
        EventBus.$emit('serviceError', e.response.status)
        this.$toast.open({
          message: e,
          type: 'is-danger',
          position: 'is-top',
          queue: false
        })
        return false
      } finally {
        this.$hideSpinner()
        this.$emit('save', this.localTags)
        this.$emit('close')
      }
    },
    getTextColor
  }
}
</script>
<style scoped>
.color-circle {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  outline: none;
  background: none;
  box-shadow: 0 0 0 2px #ccc; /* optional border effect */
}

.color-circle::-webkit-color-swatch-wrapper {
  padding: 0;
  border-radius: 50%;
}

.color-circle::-webkit-color-swatch {
  border: none;
  border-radius: 50%;
}

.tag-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  box-shadow: none; /* Remove default box shadow if desired */
  border: 1px solid var(--border-color); /* Light border to separate items */
}
</style>

<style lang="scss">
.manage-tags-modal .modal-card {
  min-width: 800px;
  width: 10vw;
}

.tag-short {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.5rem; /* Fixed width for consistency */
  height: 2rem; /* Height to make it a pill shape */
  padding: 0 0.5rem;
  border-radius: 9999px; /* Pill shape */
  color: white; /* Text color for the short tag */
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.tag-name {
  flex-grow: 1;
  margin-right: 1rem;
}

.tag-input-fake {
  min-height: 2.25em;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  padding: 2px 40px 2px 8px;
  transition: border-color 0.2s;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: #fff;
  position: relative;
  max-height: 300px; /* increase max-height if you want */
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  resize: vertical; /* <-- add this line */
}
.tag-input-fake::-webkit-scrollbar {
  height: 6px;
}
.tag-input-fake::-webkit-scrollbar-thumb {
  background: #dbdbdb;
  border-radius: 3px;
}
</style>